/**
 * nameGenerator.ts —— 取名工具（离线词库 + 生成逻辑 + AI 提示词构造与解析）
 *
 * 设计原则：
 * - 纯函数、零依赖，可在 node 环境单测
 * - 离线生成：内置精选词库（人物东/西方、地点、势力、物品），按筛选条件组合，保证不重复
 * - AI 生成：仅在用户填写补充说明时使用（调用方控制），提示词只描述任务目标与输出约束
 */

// =============== 类型 ===============

export type NameCategory = 'character' | 'place' | 'faction' | 'item';
export type NameRegion = 'east' | 'west';
export type NameGender = 'male' | 'female' | 'any';

export interface CharacterNameOptions {
  region: NameRegion;
  gender: NameGender;
  /** 名字字数（不含姓氏），1-3 */
  givenNameLength: 1 | 2 | 3;
  /** 姓氏（留空 = 随机姓氏） */
  surname?: string;
}

export interface WordNameOptions {
  /** 全名字数（含词缀），2-4 */
  length: 2 | 3 | 4;
  /** 子类筛选（地点/势力/物品），缺省 = 全部 */
  subtype?: string;
  /** 关键词约束：生成的名字必须包含该词（地点/势力/物品专用），缺省 = 不限制 */
  keyword?: string;
  /** 关键词位置约束：'end' = 关键词在末尾，'middle' = 非末尾（开头），缺省 = 不限 */
  keywordPosition?: 'end' | 'middle';
}

export type NameOptions = CharacterNameOptions | WordNameOptions;

export const NAME_CATEGORY_LABEL: Record<NameCategory, string> = {
  character: '人物',
  place: '地点',
  faction: '势力',
  item: '物品',
};

export interface NameSubtype {
  id: string;
  label: string;
}

export const PLACE_SUBTYPES: NameSubtype[] = [
  { id: 'mountain', label: '山川' },
  { id: 'water', label: '水域' },
  { id: 'town', label: '城镇' },
  { id: 'secret', label: '秘境' },
];

export const FACTION_SUBTYPES: NameSubtype[] = [
  { id: 'sect', label: '门派' },
  { id: 'family', label: '世家' },
  { id: 'gang', label: '帮会' },
  { id: 'cult', label: '教派' },
];

export const ITEM_SUBTYPES: NameSubtype[] = [
  { id: 'weapon', label: '武器' },
  { id: 'armor', label: '防具' },
  { id: 'pill', label: '丹药' },
  { id: 'book', label: '书籍' },
  { id: 'artifact', label: '法器' },
];

export const SUBTYPE_OPTIONS: Record<'place' | 'faction' | 'item', NameSubtype[]> = {
  place: PLACE_SUBTYPES,
  faction: FACTION_SUBTYPES,
  item: ITEM_SUBTYPES,
};

/** 物品名后缀 → 资料库物品类型的启发式猜测 */
export function guessItemType(name: string): string {
  if (!name) return '';
  const weapon = /[剑刀枪戟斧锤鞭锏棍棒钩环轮弓弩箭镖针]/;
  const book = /[书卷谱册图录经诀典]/;
  const pill = /[丹散丸膏香露]/;
  const herb = /[草花果芝莲参]/;
  if (weapon.test(name)) return '武器';
  if (book.test(name)) return '书籍';
  if (pill.test(name) || herb.test(name)) return '草药';
  return '';
}

// =============== 词库 ===============

function dedupe(arr: string[]): string[] {
  return Array.from(new Set(arr));
}

/** 常用单字姓 + 武侠常用复姓 */
const CN_SURNAMES = dedupe([
  '赵', '钱', '孙', '李', '周', '吴', '郑', '王', '冯', '陈', '褚', '卫', '蒋', '沈', '韩', '杨',
  '朱', '秦', '尤', '许', '何', '吕', '施', '张', '孔', '曹', '严', '华', '金', '魏', '陶', '姜',
  '戚', '谢', '邹', '喻', '柏', '水', '窦', '章', '云', '苏', '潘', '葛', '奚', '范', '彭', '郎',
  '鲁', '韦', '昌', '马', '苗', '凤', '花', '方', '俞', '任', '袁', '柳', '酆', '鲍', '史', '唐',
  '费', '廉', '岑', '薛', '雷', '贺', '倪', '汤', '滕', '殷', '罗', '毕', '郝', '邬', '安', '常',
  '乐', '于', '时', '傅', '皮', '卞', '齐', '康', '伍', '余', '元', '卜', '顾', '孟', '平', '黄',
  '和', '穆', '萧', '尹', '姚', '邵', '湛', '汪', '祁', '毛', '禹', '狄', '米', '贝', '明', '臧',
  '计', '伏', '成', '戴', '谈', '宋', '茅', '庞', '熊', '纪', '舒', '屈', '项', '祝', '董', '梁',
  '杜', '阮', '蓝', '闵', '席', '季', '麻', '强', '贾', '路', '娄', '危', '江', '童', '颜', '郭',
  '梅', '盛', '林', '刁', '钟', '徐', '邱', '骆', '高', '夏', '蔡', '田', '樊', '胡', '凌', '霍',
  '虞', '万', '支', '柯', '昝', '管', '卢', '莫', '经', '房', '裘', '缪', '干', '解', '应', '宗',
  '丁', '宣', '贲', '邓', '郁', '单', '杭', '洪', '包', '诸', '左', '石', '崔', '吉', '钮', '龚',
  '程', '嵇', '邢', '滑', '裴', '陆', '荣', '翁', '荀', '羊', '於', '惠', '甄', '曲', '家', '封',
  '芮', '羿', '储', '靳', '汲', '邴', '糜', '松', '井', '段', '富', '巫', '乌', '焦', '巴', '弓',
  '牧', '隗', '山', '谷', '车', '侯', '宓', '蓬', '全', '郗', '班', '仰', '秋', '仲', '伊', '宫',
  '宁', '仇', '栾', '暴', '甘', '厉', '戎', '祖', '武', '符', '刘', '景', '詹', '束', '龙', '叶',
  '幸', '司', '韶', '郜', '黎', '蓟', '薄', '印', '宿', '白', '怀', '蒲', '邰', '从', '鄂', '索',
  '咸', '籍', '赖', '卓', '蔺', '屠', '蒙', '池', '乔', '阴', '胥', '能', '苍', '双', '闻', '莘',
  '党', '翟', '谭', '贡', '劳', '逄', '姬', '申', '扶', '堵', '冉', '宰', '郦', '雍', '却', '璩',
  '桑', '桂', '濮', '牛', '寿', '通', '边', '扈', '燕', '冀', '郏', '浦', '尚', '农', '温', '别',
  '庄', '晏', '柴', '瞿', '阎', '充', '慕', '连', '茹', '习', '宦', '艾', '鱼', '容', '向', '古',
  '易', '慎', '戈', '廖', '庾', '终', '暨', '居', '衡', '步', '都', '耿', '满', '弘', '匡', '国',
  '文', '寇', '广', '禄', '阙', '东', '欧', '殳', '沃', '利', '蔚', '越', '夔', '隆', '师', '巩',
  '厍', '聂', '晁', '勾', '敖', '融', '冷', '訾', '辛', '阚', '那', '简', '饶', '空', '曾', '毋',
  '沙', '乜', '养', '鞠', '须', '丰', '巢', '关', '蒯', '相', '查', '后', '荆', '红', '游', '竺',
  '权', '逯', '盖', '益', '桓', '公',
  '欧阳', '太史', '端木', '上官', '司马', '东方', '独孤', '南宫', '万俟', '闻人', '夏侯', '诸葛',
  '尉迟', '公羊', '赫连', '澹台', '皇甫', '宗政', '濮阳', '公冶', '太叔', '申屠', '公孙', '慕容',
  '仲孙', '钟离', '长孙', '宇文', '司徒', '鲜于', '司空', '闾丘', '子车', '亓官', '司寇', '巫马',
  '公西', '颛孙', '壤驷', '公良', '漆雕', '乐正', '宰父', '谷梁', '拓跋', '夹谷', '轩辕', '令狐',
  '段干', '百里', '呼延', '东郭', '南门', '羊舌', '微生', '左丘', '西门', '第五', '公乘', '贯丘',
]);

/** 东方男性名用字（有寓意、有文气） */
const CN_MALE_CHARS = dedupe([
  '文', '武', '轩', '宇', '宸', '昊', '天', '泽', '渊', '铭', '谦', '越', '峰', '峻', '岳', '岩',
  '川', '河', '江', '海', '涛', '澜', '云', '霄', '风', '雷', '霆', '毅', '勇', '刚', '强', '健',
  '俊', '杰', '豪', '英', '雄', '威', '斌', '辉', '煌', '煜', '炜', '灿', '然', '儒', '雅', '彦',
  '贤', '哲', '睿', '智', '聪', '明', '达', '通', '远', '途', '程', '鹏', '鸿', '鹤', '龙', '虎',
  '麟', '骥', '骁', '翱', '翔', '飞', '扬', '凯', '胜', '捷', '邦', '国', '兴', '盛', '荣', '华',
  '贵', '禄', '祥', '瑞', '安', '宁', '康', '泰', '和', '平', '顺', '恒', '久', '志', '愿', '望',
  '诚', '信', '义', '礼', '智', '仁', '德', '道', '真', '善', '美', '明', '光', '正', '大', '崇',
  '高', '尚', '敬', '仰', '慕', '思', '念', '怀', '忆', '承', '启', '伯', '仲', '叔', '元', '亨',
  '利', '贞', '乾', '坤', '玄', '极', '道', '仙', '灵', '清', '玉', '白', '广', '宏', '伟', '壮',
  '丽', '奇', '昭', '旭', '东', '升', '阳', '曜', '曦', '晨', '晓', '曙', '晖', '旻', '晟', '昶',
  '焕', '燊', '焱', '森', '霖', '鑫', '淼', '垚', '磊', '磐', '峥', '嵘', '屹', '立', '昂', '啸',
  '御', '策', '谋', '略', '韬', '显', '耀', '承', '志',
]);

/** 东方女性名用字 */
const CN_FEMALE_CHARS = dedupe([
  '婉', '清', '雪', '月', '兰', '梅', '竹', '菊', '荷', '莲', '薇', '萱', '芷', '若', '蕊', '婷',
  '静', '淑', '雅', '娴', '秀', '丽', '美', '妙', '玲', '珑', '娟', '娥', '婵', '姝', '婕', '嫣',
  '妍', '姿', '容', '颜', '笑', '语', '歌', '舞', '琴', '棋', '书', '画', '诗', '词', '赋', '韵',
  '律', '音', '乐', '笙', '箫', '笛', '瑟', '瑶', '瑛', '莹', '玥', '琳', '璇', '琦', '瑾', '瑜',
  '璐', '珊', '瑚', '珍', '珠', '玉', '环', '佩', '钗', '镯', '铃', '香', '馨', '芳', '菲', '芸',
  '蓉', '芙', '蕾', '露', '霜', '冰', '晶', '洁', '素', '碧', '青', '翠', '黛', '绯', '胭', '霞',
  '虹', '霓', '彩', '梦', '幻', '影', '思', '念', '忆', '情', '柔', '温', '暖', '恬', '淡', '舒',
  '畅', '愉', '悦', '欣', '怡', '慧', '敏', '捷', '巧', '灵', '俏', '娇', '媚', '文', '静', '娴',
  '端', '庄', '大', '方', '典', '雅', '高', '贵', '气', '质', '华', '丽', '优', '飘', '逸', '动',
  '可', '人', '乖', '巧', '伶', '俐', '聪', '贤', '温', '柔', '体', '贴', '善', '良', '纯', '真',
  '天', '真', '烂', '漫', '活', '泼', '开', '朗', '阳', '光', '灿', '烂', '明', '媚', '娇', '艳',
  '瑰', '绚', '多', '姿', '婀', '娜', '娉', '含', '羞', '矜', '持', '恬', '静', '淡', '然', '从',
  '容', '知', '意', '念', '语', '黛', '眉', '眸', '波', '盈', '皓', '皎', '瑶', '琼', '琬', '琰',
]);

/** 西方男性名 */
const EN_MALE_NAMES = dedupe([
  'Liam', 'Noah', 'Oliver', 'Elijah', 'James', 'William', 'Benjamin', 'Lucas', 'Henry', 'Theodore',
  'Jack', 'Levi', 'Alexander', 'Jackson', 'Mateo', 'Daniel', 'Michael', 'Owen', 'Samuel', 'Sebastian',
  'David', 'Carter', 'Jayden', 'John', 'Luke', 'Anthony', 'Isaac', 'Dylan', 'Wyatt', 'Andrew',
  'Joshua', 'Christopher', 'Gabriel', 'Thomas', 'Julian', 'Leo', 'Landon', 'Hunter', 'Arthur', 'Felix',
  'Hugo', 'Oscar', 'Ethan', 'Aiden', 'Mason', 'Logan', 'Nathan', 'Ryan', 'Eric', 'Adam',
  'Aaron', 'Brian', 'Carl', 'Dennis', 'Edward', 'Frank', 'George', 'Henry', 'Ian', 'Kevin',
  'Marcus', 'Neil', 'Patrick', 'Raymond', 'Simon', 'Victor', 'Walter', 'Xavier', 'Zachary', 'Silas',
  'Elias', 'Jasper', 'Finn', 'Miles', 'Rhys', 'Caleb', 'Evan', 'Gavin', 'Ivan', 'Kai',
]);

/** 西方女性名 */
const EN_FEMALE_NAMES = dedupe([
  'Olivia', 'Emma', 'Charlotte', 'Amelia', 'Sophia', 'Isabella', 'Mia', 'Evelyn', 'Harper', 'Luna',
  'Aurora', 'Grace', 'Ella', 'Scarlett', 'Chloe', 'Lily', 'Hannah', 'Aria', 'Emily', 'Abigail',
  'Elizabeth', 'Sofia', 'Camila', 'Penelope', 'Layla', 'Riley', 'Zoey', 'Nora', 'Hazel', 'Violet',
  'Stella', 'Paisley', 'Avery', 'Aubrey', 'Audrey', 'Madison', 'Victoria', 'Samantha', 'Claire', 'Ruby',
  'Alice', 'Rose', 'Ivy', 'Jade', 'Pearl', 'Daisy', 'Iris', 'Amber', 'Cora', 'Elsie',
  'Flora', 'Georgia', 'Holly', 'June', 'Lillian', 'Mabel', 'Naomi', 'Olive', 'Phoebe', 'Quinn',
  'Rosie', 'Sadie', 'Tessa', 'Ursula', 'Vera', 'Willa', 'Yvonne', 'Zara', 'Beatrice', 'Cecilia',
  'Diana', 'Eleanor', 'Fiona', 'Genevieve', 'Helena', 'Isla', 'Josephine', 'Katherine', 'Louisa', 'Margot',
]);

/** 西方姓氏 */
const EN_SURNAMES = dedupe([
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez',
  'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin',
  'Lee', 'Perez', 'Thompson', 'White', 'Harris', 'Sanchez', 'Clark', 'Ramirez', 'Lewis', 'Robinson',
  'Walker', 'Young', 'Allen', 'King', 'Wright', 'Scott', 'Torres', 'Nguyen', 'Hill', 'Flores',
  'Green', 'Adams', 'Nelson', 'Baker', 'Hall', 'Rivera', 'Campbell', 'Mitchell', 'Carter', 'Roberts',
  'Gomez', 'Phillips', 'Evans', 'Turner', 'Diaz', 'Parker', 'Cruz', 'Edwards', 'Collins', 'Reyes',
  'Stewart', 'Morris', 'Murphy', 'Cook', 'Rogers', 'Morgan', 'Cooper', 'Peterson', 'Bailey', 'Reed',
  'Kelly', 'Howard', 'Ramos', 'Kim', 'Cox', 'Ward', 'Richardson', 'Watson', 'Brooks', 'Bennett',
  'Gray', 'James', 'Hughes', 'Price', 'Myers', 'Long', 'Foster', 'Sanders', 'Ross', 'Powell',
  'Sullivan', 'Russell', 'Jenkins', 'Perry', 'Butler', 'Barnes', 'Fisher', 'Henderson', 'Coleman', 'Simmons',
]);

// ── 地点（按子类分组） ──────────────────────────────────────────

const PLACE_BY_KIND: Record<string, { prefixes: string[]; suffixes: string[] }> = {
  mountain: {
    prefixes: dedupe([
      '青', '苍', '玄', '紫', '白', '翠', '黛', '碧', '金', '银', '玉', '雪', '霜', '风', '云', '雷',
      '星', '月', '日', '山', '谷', '幽', '冥', '灵', '圣', '神', '仙', '龙', '凤', '虎', '鹰', '雁',
      '鹤', '桃', '杏', '梅', '兰', '竹', '松', '柏', '柳', '枫', '孤', '远', '寒', '清', '空', '虚',
      '极',
      '太初', '鸿蒙', '落霞', '流云', '碧落', '苍梧', '太虚', '幽冥', '紫霄', '青莲', '明月', '清风',
      '白雪', '寒江', '孤山', '远山', '昆仑', '蓬莱', '天阙',
    ]),
    suffixes: dedupe([
      '山', '峰', '崖', '岭', '岩', '石', '谷', '林', '原', '丘', '岗', '峦', '嶂', '壁', '台', '巅',
      '麓', '坡', '漠',
      '雪原', '幽谷', '石林', '悬崖', '荒漠', '山脉', '丘陵', '深渊', '绝壁', '断崖', '云海', '林海',
    ]),
  },
  water: {
    prefixes: dedupe([
      '碧', '沧', '寒', '清', '翠', '黛', '青', '白', '金', '银', '玉', '雪', '霜', '风', '云', '雷',
      '月', '星', '天', '海', '江', '河', '湖', '溪', '泉', '潭', '泽', '幽', '灵', '圣', '龙', '凤',
      '鹤', '雁', '桃', '杏', '柳', '枫', '莲', '荷', '芦', '苇', '孤', '远', '明', '静', '空',
      '太初', '落霞', '流云', '碧落', '苍梧', '明月', '清风', '寒江', '洞庭', '沧海', '烟波', '秋水',
      '长河',
    ]),
    suffixes: dedupe([
      '江', '河', '湖', '海', '溪', '泉', '潭', '泽', '洲', '湾', '港', '渡', '池', '塘', '涧', '浜',
      '浦', '滩',
      '水乡', '渡口', '月湾', '群岛', '碧波', '清溪', '深渊', '大河', '海岸', '湖畔', '瀑布', '泉眼',
    ]),
  },
  town: {
    prefixes: dedupe([
      '青', '苍', '玄', '紫', '白', '金', '银', '玉', '雪', '风', '云', '雷', '星', '月', '天', '地',
      '灵', '圣', '神', '仙', '龙', '凤', '虎', '鹰', '鹤', '桃', '杏', '梅', '兰', '竹', '松', '柳',
      '枫', '桂', '沙', '石', '岩', '孤', '远',
      '太初', '落霞', '流云', '碧落', '苍梧', '明月', '清风', '白雪', '江南', '塞北', '漠北', '西域',
      '中州', '东海', '西山', '昆仑', '蓬莱', '桃花', '杏花', '梅花', '枫桥', '兰亭',
    ]),
    suffixes: dedupe([
      '城', '镇', '村', '寨', '堡', '关', '口', '坊', '街', '巷', '驿', '馆', '市', '郡', '府', '州',
      '国', '庄', '园',
      '古城', '渡口', '山庄', '水乡', '边城', '重镇', '古村', '要塞', '集市', '官道',
    ]),
  },
  secret: {
    prefixes: dedupe([
      '幽', '冥', '玄', '虚', '幻', '极', '太', '天', '地', '灵', '圣', '神', '仙', '龙', '凤',
      '麒麟', '天罡', '太初', '混沌', '鸿蒙', '元始', '太虚', '幽冥', '紫霄', '青莲', '白玉', '琉璃',
      '翡翠', '明月', '清风', '九幽', '黄泉', '碧落', '天阙', '蓬莱', '方丈', '瀛洲', '昆仑', '桃花',
      '秘境', '洞天', '福地',
    ]),
    suffixes: dedupe([
      '窟', '府', '境', '域', '冢', '墟', '界',
      '仙境', '幽谷', '洞天', '福地', '圣地', '迷窟', '古墓', '禁地', '神域', '秘境', '仙府', '灵境',
      '魔窟', '鬼域', '异界', '天界', '灵山', '圣山', '幽林', '仙踪',
    ]),
  },
};

// ── 势力（按子类分组） ──────────────────────────────────────────

const COMMON_FACTION_PREFIXES = dedupe([
  '青', '苍', '玄', '紫', '白', '赤', '黑', '金', '银', '玉', '天', '地', '乾', '坤', '太', '阴',
  '阳', '星', '辰', '日', '月', '风', '云', '雷', '火', '冰', '霜', '雪', '寒', '幽', '冥', '暗',
  '影', '夜', '魔', '妖', '鬼', '邪', '圣', '神', '仙', '灵', '龙', '凤', '虎', '豹', '鹰', '狼',
  '鹤', '燕', '雀', '鹏', '鸿',
  '天罡', '地煞', '紫微', '七星', '北斗', '南斗', '东方', '西域', '南疆', '北荒', '中州', '五行',
  '八卦', '九宫', '十方', '千古', '万世', '永恒', '太初', '元始', '上清', '太清', '玉清', '碧落',
  '黄泉', '九幽', '玄冥', '赤焰', '冰魄', '紫霄', '青莲', '白玉', '琉璃', '翡翠', '珊瑚', '明珠',
  '夜光', '幽兰', '墨梅', '寒松', '苍梧', '落霞', '秋水', '长天', '一剑', '无双', '独孤', '神剑',
  '快刀', '红尘', '笑傲', '江湖', '天下', '霸王', '铁骑', '龙骑', '虎卫', '鹰卫', '锦衣', '绣衣',
  '血滴', '青衣', '红袖', '烟雨', '风陵', '悦来', '同福', '天机', '百晓', '万事', '神机', '霹雳',
  '轰天', '九天', '紫霞', '瑶池', '蓬莱', '昆仑', '峨眉', '武当', '少林', '嵩山', '华山', '衡山',
  '恒山', '泰山', '青城', '崆峒', '点苍', '唐门', '蜀中', '江南', '塞北', '漠北', '西域', '苗疆',
  '南诏', '大理', '西夏', '蒙古', '鲜卑', '匈奴', '突厥', '回鹘', '吐蕃', '契丹', '女真', '东瀛',
  '高丽', '琉球', '暹罗', '天竺', '波斯', '大食', '罗马',
]);

const FACTION_BY_KIND: Record<string, { prefixes: string[]; suffixes: string[] }> = {
  sect: {
    prefixes: COMMON_FACTION_PREFIXES,
    suffixes: dedupe([
      '门', '派', '宗', '观', '寺', '坛', '堂', '会', '盟',
      '剑派', '刀派', '武馆', '书院', '医馆',
    ]),
  },
  family: {
    prefixes: CN_SURNAMES,
    suffixes: dedupe(['府', '世家', '家族', '宗族', '门阀', '望族']),
  },
  gang: {
    prefixes: COMMON_FACTION_PREFIXES,
    suffixes: dedupe([
      '帮', '会', '盟', '堂', '团', '部', '社', '馆', '营',
      '帮会', '商会', '钱庄', '镖局', '联盟', '公会', '行会', '义军', '联军', '佣兵团', '骑士团',
    ]),
  },
  cult: {
    prefixes: dedupe([
      '魔', '妖', '鬼', '邪', '冥', '暗', '血', '阴', '幽', '黑', '毒', '蛊', '尸', '魂', '煞',
      '天', '地', '圣', '神', '仙', '灵', '白', '青', '红', '紫', '明', '无', '有', '太', '元',
      '九', '千', '万',
      '天罡', '玄冥', '赤焰', '冰魄', '紫霄', '九幽', '黄泉', '幽冥', '焚天', '噬魂', '断魂', '摄魂',
      '锁魂', '夺命', '索命', '追魂', '泣血', '血滴', '幽灵', '白骨', '黑莲', '白莲', '红莲', '青莲',
      '冥王', '阎罗', '无常', '修罗', '夜叉', '罗刹', '金刚', '明王', '欢喜', '慈悲', '普渡', '轮回',
      '往生',
    ]),
    suffixes: dedupe([
      '教', '宗', '殿', '坛', '司',
      '教廷', '圣殿', '神教', '魔教', '冥教', '鬼教', '血教', '圣教', '邪教', '神宗', '魔宗', '王朝',
      '帝国',
    ]),
  },
};

// ── 物品（按子类分组） ──────────────────────────────────────────

const ITEM_MATERIALS_COMMON = dedupe([
  '金', '银', '铁', '铜', '玉', '石', '木', '冰', '火', '雷', '风', '土', '水', '灵', '仙', '神',
  '龙', '凤', '玄', '紫', '青', '白', '赤', '黑', '墨', '雪', '霜', '月', '星', '日', '天', '地',
  '玄铁', '寒铁', '陨铁', '精钢', '百炼钢', '赤铜', '紫铜', '青铜', '黄铜', '白金', '黄金', '赤金',
  '白银', '玄冰', '寒玉', '暖玉', '白玉', '青玉', '墨玉', '血玉', '翡翠', '玛瑙', '琉璃', '水晶',
  '星辰', '陨石', '龙鳞', '凤羽', '麒麟角', '玄武壳', '蛟龙筋', '天蚕丝', '冰蚕丝', '火蚕丝', '金蚕丝',
  '云锦', '天衣', '紫霞', '流云', '碧落', '苍梧', '太虚', '幽冥', '混沌', '鸿蒙', '太极', '阴阳',
  '五行', '八卦', '九宫', '七星', '北斗', '紫微', '太乙', '九天', '菩提', '舍利', '金刚', '玉净',
  '羊脂', '田黄', '鸡血', '祖母绿', '蓝宝石', '红宝石', '猫眼', '月光石', '星石', '火石', '冰晶',
  '雷晶', '风晶', '土晶', '圣晶', '魔晶', '龙晶', '凤晶', '灵晶', '仙晶', '神晶', '妖晶', '魂晶',
  '沉香', '檀香', '龙涎', '麝香', '千年', '万年', '九幽', '玄冥', '赤焰', '冰魄', '紫霄', '青莲',
  '琉璃', '翡翠', '珊瑚', '明珠', '夜光', '幽兰', '墨梅', '寒松', '苍梧', '落霞', '秋水', '长天',
  '惊鸿', '游龙', '鸣凤', '飞燕', '断魂', '摄魂', '锁魂', '噬魂', '追魂', '夺命', '索命', '催命',
]);

const ITEM_BY_KIND: Record<string, { prefixes: string[]; suffixes: string[] }> = {
  weapon: {
    prefixes: ITEM_MATERIALS_COMMON,
    suffixes: dedupe([
      '剑', '刀', '枪', '戟', '斧', '锤', '鞭', '锏', '棍', '棒', '钩', '环', '轮', '弓', '弩', '箭',
      '镖', '针', '刺', '叉', '刃',
      '匕首', '长剑', '短刃', '铁剑', '神兵', '利刃', '宝刀', '名剑', '弯弓', '羽箭', '暗器', '飞镖',
      '毒针', '长枪', '铁锤', '巨斧', '软鞭', '铁链', '重剑', '利剑', '双剑', '单刀', '朴刀', '大刀',
      '钢枪', '银枪', '铁棍',
    ]),
  },
  armor: {
    prefixes: ITEM_MATERIALS_COMMON,
    suffixes: dedupe([
      '甲', '盔', '盾', '靴', '带', '链', '镯', '铠', '胄', '披', '袍', '衣', '衫', '服', '履', '鞋',
      '袜', '冠', '巾', '帽',
      '玄甲', '软甲', '护腕', '战靴', '宝甲', '法袍', '道袍', '袈裟', '霞帔', '金甲', '银甲', '铁甲',
      '藤甲', '护膝', '护臂',
    ]),
  },
  pill: {
    prefixes: ITEM_MATERIALS_COMMON,
    suffixes: dedupe([
      '丹', '散', '丸', '膏', '香', '露', '粉', '汤', '液', '浆', '酥', '霜',
      '仙丹', '灵药', '神药', '秘药', '毒药', '解药', '补药', '金丹', '灵丹', '圣药', '玉露', '琼浆',
    ]),
  },
  book: {
    prefixes: ITEM_MATERIALS_COMMON,
    suffixes: dedupe([
      '书', '卷', '谱', '册', '图', '经', '典', '诀', '文', '录', '志', '传', '记', '疏', '注', '解',
      '篇', '章',
      '秘籍', '功法', '心法', '真经', '阵图', '剑谱', '刀谱', '拳谱', '医书', '药典', '毒经', '兵书',
      '史册', '画卷', '禁书', '遗书', '密录', '笔记', '手札', '信笺', '圣旨', '诏书',
    ]),
  },
  artifact: {
    prefixes: ITEM_MATERIALS_COMMON,
    suffixes: dedupe([
      '珠', '镜', '印', '鼎', '炉', '塔', '钟', '琴', '箫', '笛', '扇', '笔', '砚', '符', '旗', '幡',
      '伞', '玉', '佩', '环', '玦', '璧', '圭', '璋', '笏', '简', '牍', '杖', '杵', '钵', '网', '索',
      '绳', '帐', '帘', '罩', '梭', '剪', '尺', '秤', '锁', '钥', '铃', '鼓', '锣', '磬',
      '玉佩', '宝珠', '灵珠', '符箓', '灵符', '香炉', '古琴', '玉箫', '宝镜', '铜镜', '金印', '玉玺',
      '金锁', '锦囊', '香囊', '宝盒', '玉匣', '罗盘', '拂尘', '禅杖', '锡杖', '纳戒',
    ]),
  },
};

// =============== 随机工具 ===============

function randInt(n: number): number {
  return Math.floor(Math.random() * n);
}

function pick<T>(arr: T[]): T {
  return arr[randInt(arr.length)];
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = randInt(i + 1);
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// =============== 人物生成 ===============

function charPool(gender: NameGender, region: NameRegion): string[] {
  if (region === 'east') {
    if (gender === 'male') return CN_MALE_CHARS;
    if (gender === 'female') return CN_FEMALE_CHARS;
    return [...CN_MALE_CHARS, ...CN_FEMALE_CHARS];
  }
  if (gender === 'male') return EN_MALE_NAMES;
  if (gender === 'female') return EN_FEMALE_NAMES;
  return [...EN_MALE_NAMES, ...EN_FEMALE_NAMES];
}

function generateEastName(opts: CharacterNameOptions, surnamePool: string[]): string | null {
  const surname = pick(surnamePool);
  const pool = charPool(opts.gender, 'east');
  const len = opts.givenNameLength;
  const surnameChars = new Set(surname.split(''));

  // 名用字：排除与姓氏相同的字，避免「李小李」式组合
  const usable = pool.filter(c => !surnameChars.has(c));
  if (usable.length < len) return null;

  const given = shuffle(usable).slice(0, len).join('');
  // 单字名不与姓氏完全相同
  if (len === 1 && given === surname) return null;
  return surname + given;
}

function generateWestName(opts: CharacterNameOptions, surnamePool: string[]): string {
  const given = pick(charPool(opts.gender, 'west'));
  const family = opts.surname?.trim() || pick(surnamePool);
  return `${given} ${family}`;
}

function generateCharacterNames(opts: CharacterNameOptions, count: number): string[] {
  const result: string[] = [];
  const seen = new Set<string>();
  let attempts = 0;
  const surnamePool = opts.surname?.trim() ? [opts.surname.trim()] : CN_SURNAMES;
  const maxAttempts = count * 40 + 500;

  while (result.length < count && attempts < maxAttempts) {
    attempts++;
    const name = opts.region === 'east'
      ? generateEastName(opts, surnamePool)
      : generateWestName(opts, EN_SURNAMES);
    if (!name || seen.has(name)) continue;
    seen.add(name);
    result.push(name);
  }
  return result;
}

// =============== 地点/势力/物品生成 ===============

function wordBank(kind: 'place' | 'faction' | 'item', subtype?: string): { prefixes: string[]; suffixes: string[] } {
  const byKind = kind === 'place' ? PLACE_BY_KIND : kind === 'faction' ? FACTION_BY_KIND : ITEM_BY_KIND;
  const bank = subtype ? byKind[subtype] : undefined;
  if (bank) return bank;
  return {
    prefixes: dedupe(Object.values(byKind).flatMap(b => b.prefixes)),
    suffixes: dedupe(Object.values(byKind).flatMap(b => b.suffixes)),
  };
}

/**
 * 关键词模式：生成的名字必须包含 keyword。
 *
 * 位置约束（keywordPosition）：
 * - 'end'    关键字在末尾：head + keyword（head 长度 = 总长 - 关键字长）
 * - 'middle' 关键字在非末尾（含开头、中间）：head + keyword + tail，tail 长度 ≥ 1
 * - 缺省     关键字位置不限：末尾与非末尾槽位混合
 *
 * 「位置槽位」模型：遍历关键字所有合法起始位置（0 ~ otherLen），
 * 每个槽位收集该位置所需的 head（关键字前）/ tail（关键字后）词池，
 * 生成时随机取一个槽位 —— 确保 middle 模式关键字可出现在除末尾外的任意位置。
 *
 * 例：3 字名字 + 1 字关键字「雪」：
 *   位置0（开头）："" + 雪 + 2字suffix → 雪幽谷
 *   位置1（中间）：1字prefix + 雪 + 1字suffix → 寒雪峰
 *   位置2（末尾）：2字prefix + 雪 + "" → 明月雪（仅 end/不限 模式）
 */
function generateKeywordNames(
  kind: 'place' | 'faction' | 'item',
  length: number,
  keyword: string,
  count: number,
  subtype?: string,
  keywordPosition?: 'end' | 'middle',
): string[] {
  const { prefixes, suffixes } = wordBank(kind, subtype);
  const result: string[] = [];
  const seen = new Set<string>();
  let attempts = 0;
  const maxAttempts = count * 80 + 1500;

  if (keyword.length === length) {
    seen.add(keyword);
    result.push(keyword);
  }
  if (keyword.length >= length) return result;

  const otherLen = length - keyword.length;

  // 构建「位置槽位」：每个槽位描述关键字在某个起始位置时所需的 head/tail 词池
  const slots: { headPool: string[]; tailPool: string[] }[] = [];
  for (let pos = 0; pos <= otherLen; pos++) {
    const headLen = pos;
    const tailLen = otherLen - pos;
    // 'end'：关键字在末尾 → tail 必须为空；'middle'：非末尾 → tail 必须非空
    if (keywordPosition === 'end' && tailLen !== 0) continue;
    if (keywordPosition === 'middle' && tailLen === 0) continue;
    const headPool = headLen === 0 ? [''] : prefixes.filter(p => p.length === headLen);
    const tailPool = tailLen === 0 ? [''] : suffixes.filter(s => s.length === tailLen);
    if (headPool.length > 0 && tailPool.length > 0) {
      slots.push({ headPool, tailPool });
    }
  }
  if (slots.length === 0) return result;

  while (result.length < count && attempts < maxAttempts) {
    attempts++;
    const slot = slots[Math.floor(Math.random() * slots.length)];
    const name = pick(slot.headPool) + keyword + pick(slot.tailPool);
    if (seen.has(name)) continue;
    seen.add(name);
    result.push(name);
  }
  return result;
}

function generateWordNames(kind: 'place' | 'faction' | 'item', length: number, count: number, subtype?: string, keyword?: string, keywordPosition?: 'end' | 'middle'): string[] {
  const kw = keyword?.trim();
  if (kw) return generateKeywordNames(kind, length, kw, count, subtype, keywordPosition);
  const { prefixes, suffixes } = wordBank(kind, subtype);
  const oneCharPrefixes = prefixes.filter(p => p.length === 1);
  const twoCharPrefixes = prefixes.filter(p => p.length === 2);
  const oneCharSuffixes = suffixes.filter(s => s.length === 1);
  const twoCharSuffixes = suffixes.filter(s => s.length === 2);

  const result: string[] = [];
  const seen = new Set<string>();
  let attempts = 0;
  const maxAttempts = count * 40 + 500;

  while (result.length < count && attempts < maxAttempts) {
    attempts++;
    const prefixPool = length === 2 ? oneCharPrefixes : [...oneCharPrefixes, ...twoCharPrefixes];
    const suffixPool = length === 4
      ? twoCharSuffixes
      : length === 3
        ? [...oneCharSuffixes, ...twoCharSuffixes]
        : oneCharSuffixes;
    if (prefixPool.length === 0 || suffixPool.length === 0) break;

    const prefix = pick(prefixPool);
    const suffix = pick(suffixPool);
    if (prefix.length + suffix.length !== length) continue;
    const name = prefix + suffix;
    if (seen.has(name)) continue;
    seen.add(name);
    result.push(name);
  }
  return result;
}

// =============== 对外生成入口 ===============

export function generateNames(category: NameCategory, options: NameOptions, count = 30): string[] {
  if (category === 'character') {
    return generateCharacterNames(options as CharacterNameOptions, count);
  }
  const o = options as WordNameOptions;
  return generateWordNames(category, o.length, count, o.subtype, o.keyword, o.keywordPosition);
}

// =============== AI 生成：提示词构造与结果解析 ===============

const AI_NAME_SYSTEM_PROMPT =
  '你是一位资深小说取名助手。请根据要求生成名字：只输出名字本身，每行一个；不要编号、不要引号、不要解释、不要多余文字。';

export { AI_NAME_SYSTEM_PROMPT };

export function buildAINamePrompt(category: NameCategory, options: NameOptions, note: string): string {
  const label = NAME_CATEGORY_LABEL[category];
  const constraints: string[] = [];

  if (category === 'character') {
    const o = options as CharacterNameOptions;
    constraints.push(o.region === 'east' ? '东方风格' : '西方风格');
    constraints.push(o.gender === 'male' ? '男性' : o.gender === 'female' ? '女性' : '性别不限');
    constraints.push(`名字（不含姓氏）${o.givenNameLength} 个字`);
    const surname = o.surname?.trim();
    constraints.push(surname ? `姓氏为「${surname}」` : '姓氏不限');
  } else {
    const o = options as WordNameOptions;
    constraints.push(`${o.length} 个字`);
    if (o.subtype) {
      const sub = SUBTYPE_OPTIONS[category as 'place' | 'faction' | 'item'].find(s => s.id === o.subtype);
      if (sub) constraints.push(`类型：${sub.label}`);
    }
    const keyword = o.keyword?.trim();
    if (keyword) {
      if (o.keywordPosition === 'end') constraints.push(`「${keyword}」在名字末尾`);
      else if (o.keywordPosition === 'middle') constraints.push(`「${keyword}」不在名字末尾`);
      else constraints.push(`名字中必须包含「${keyword}」`);
    }
  }

  const lines = [`请生成 20 个${label}名字。`, `要求：${constraints.join('、')}。`];
  if (note.trim()) lines.push(`补充说明：${note.trim()}`);
  return lines.join('\n');
}

const LINE_CLEAN_REGEX = /^\s*(?:\d+[.、)）]\s*|[-*•·]\s*)?["'「『]?(.*?)["'」』]?\s*$/;

/** 解析 AI 输出：每行一个名字，去掉编号/引号/项目符号，去重，上限 40 */
export function parseAINames(content: string, limit = 40): string[] {
  if (!content) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of content.split('\n')) {
    const m = raw.match(LINE_CLEAN_REGEX);
    const name = (m ? m[1] : raw).trim().replace(/\s+/g, ' ');
    if (!name || name.length > 40) continue;
    if (seen.has(name)) continue;
    seen.add(name);
    result.push(name);
    if (result.length >= limit) break;
  }
  return result;
}
